import moment from "moment";
import PropTypes from "prop-types";
import React, { Component } from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import { compact, filter, forEach, get, includes, isEqual, keys, pickBy, some, sortBy, toLower, values } from "lodash";

import AutosuggestCNPJ from "components/EnergyCompany/Autosuggest/AutosuggestCNPJ";
import BreakEven from "./partials/BreakEven";
import CargaEGeracao from "./partials/CargaEGeracao.jsx";
import CCEEData from "./partials/CCEEData";
import CNAE from "./partials/CNAE";
import ConsolidatedAccounting from "./partials/ConsolidatedAccounting";
import ConsumptionChart from "./partials/ConsumptionChart";
import CorporateStructure from "./partials/CorporateStructure";
import DRE from "./partials/DRE";
import Informations from "./partials/Informations";
import LinearProgress from '@material-ui/core/LinearProgress';
import MediaMovel from "./partials/MediaMovel";
import Mapa from "./partials/Mapa";
import RiscoChart from "./partials/RiscoChart";
import SazoFlex from "./partials/SazoFlex";
import TabelaBalanco from "./partials/TabelaBalanco";
import { getCativoInfo, getCompanyInfo } from "services/companies";
import { removeNonDigits, scrollToElement } from "services/utils";

const styles = {
  autosuggestWrapper: {
    marginBottom: "25px",
  },
};

class Dashboard extends Component {
  state = {
    allProfilesChecked: true,
    cnpjDados: undefined,
    error: undefined,
    loading: false,
    profilesChecked: {},
  };

  componentDidMount() {
    this.handleSearch(this.props.selected);
    // NOTE: seTimeout é necessário senão o elem.scrollIntoView
    // não funciona dentro do componentDidUpdate.
    if (this.props.simplified) {
      setTimeout(() => scrollToElement(this.dashboard), 200);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevProps.selected, this.props.selected) && this.props.selected) {
      this.handleSearch(this.props.selected);
      // NOTE: Ditto
      setTimeout(() => scrollToElement(this.dashboard), 200);
    }

    if (!isEqual(prevState.cnpjDados, this.state.cnpjDados) && this.state.cnpjDados) {
      // NOTE: Ditto
      setTimeout(() => scrollToElement(this.dashboardLoaded), 200);
    }
  }

  getAllProfilesIds(data) {
    const profilesChecked = {};
    if (!data.empresa.agents.length) return profilesChecked;
    forEach(data.empresa.agents[0].agentProfiles, profile => profilesChecked[profile.id] = true);
    return profilesChecked;
  }

  getCheckedProfiles() {
    const { cnpjDados, profilesChecked } = this.state;
    const checkedProfilesIds = keys(pickBy(profilesChecked));
    return filter(cnpjDados.empresa.agents[0].agentProfiles, profile => includes(checkedProfilesIds, profile.id));
  }

  getConsolidatedFromProfiles() {
    const { cnpjDados } = this.state;
    const monthsKeys = keys(cnpjDados.empresa.agents[0].consolidatedEnergy);
    const months = sortBy(monthsKeys, month => moment(month).unix());

    let energy = {};
    let accounting = {};
    let balanco = {};

    forEach(months, month => {
      accounting[month] = {
        ajusteAlivioRetroativo: 0,
        ajusteExposicaoFinanceira: 0,
        ajusteMCSDExPost: 0,
        ajusteRecontabilizacoes: 0,
        ajustes: 0,
        compensacaoMRE: 0,
        ecd: 0,
        ecqa: 0,
        eccen: 0,
        eccgf: 0,
        ecita: 0,
        errh: 0,
        edesc: 0,
        efeitos: 0,
        encargos: 0,
        exposicaoMCP: 0,
        excedenteER: 0,
        resultadoFinal: 0,
      };

      energy[month] = {
        garantiaFisica: 0,
        lastro: 0,
        recurso: 0,
        recursoConsiderandoGF: 0,
        requisito: 0,
        saldo: 0,
      };

      balanco[month] = {
        consumoLiquido: 0,
        perdasConsumo: 0,
        consumo: 0,
        comprasSemProinfa: 0,
        proinfa: 0,
        vendas: 0,
        saldoContratos: 0,
        saldo: 0,
      };

      forEach(this.getCheckedProfiles(), ({ accountingValues, energiesValues }) => {
        // Coluna de ajustes e valores do tooltip
        accounting[month].ajustes += get(accountingValues, `${month}.ajustes`, 0);
        accounting[month].ajusteAlivioRetroativo += get(accountingValues, `${month}.ajusteAlivioRetroativo`, 0);
        accounting[month].ajusteExposicaoFinanceira += get(accountingValues, `${month}.ajusteExposicaoFinanceira`, 0);
        accounting[month].ajusteMCSDExPost += get(accountingValues, `${month}.ajusteMCSDExPost`, 0);
        accounting[month].ajusteRecontabilizacoes += get(accountingValues, `${month}.ajusteRecontabilizacoes`, 0);

        // Coluna de efeitos e valores do tooltip
        accounting[month].efeitos += get(accountingValues, `${month}.efeitos`, 0);
        accounting[month].ecd += get(accountingValues, `${month}.ecd`, 0);
        accounting[month].ecqa += get(accountingValues, `${month}.ecqa`, 0);
        accounting[month].eccen += get(accountingValues, `${month}.eccen`, 0);
        accounting[month].eccgf += get(accountingValues, `${month}.eccgf`, 0);
        accounting[month].ecita += get(accountingValues, `${month}.ecita`, 0);
        accounting[month].errh += get(accountingValues, `${month}.errh`, 0);
        accounting[month].edesc += get(accountingValues, `${month}.edesc`, 0);

        accounting[month].compensacaoMRE += get(accountingValues, `${month}.compensacaoMRE`, 0);
        accounting[month].encargos += get(accountingValues, `${month}.encargos`, 0);
        accounting[month].exposicaoMCP += get(accountingValues, `${month}.exposicaoMCP`, 0);
        accounting[month].excedenteER += get(accountingValues, `${month}.resultadoExcedenteFinanceiroEnergiaReserva`, 0);
        accounting[month].resultadoFinal += get(accountingValues, `${month}.resultadoFinal`, 0);

        energy[month].garantiaFisica += get(energiesValues, `${month}.garantiaFisica`, 0);
        energy[month].lastro += get(energiesValues, `${month}.lastro`, 0);
        energy[month].recurso += get(energiesValues, `${month}.recurso`, 0);
        energy[month].recursoConsiderandoGF += get(energiesValues, `${month}.recursoConsiderandoGF`, 0);
        energy[month].requisito += get(energiesValues, `${month}.requisito`, 0);
        energy[month].saldo += get(energiesValues, `${month}.saldo`, 0);

        // Tabela de Balanço
        balanco[month].consumoLiquido += get(energiesValues, `${month}.consumoLiquido`, 0);
        balanco[month].perdasConsumo += get(energiesValues, `${month}.perdasConsumo`, 0);
        balanco[month].consumo += get(energiesValues, `${month}.consumo`, 0);
        balanco[month].comprasSemProinfa += get(energiesValues, `${month}.comprasSemProinfa`, 0);
        balanco[month].proinfa += get(energiesValues, `${month}.proinfa`, 0);
        balanco[month].vendas += get(energiesValues, `${month}.vendas`, 0);
        balanco[month].saldoContratos += get(energiesValues, `${month}.saldoContratos`, 0);
        balanco[month].saldo += get(energiesValues, `${month}.saldo`, 0);
      });
    });

    return { accounting, energy, balanco };
  }

  handleAllProfilesCheck = e => {
    this.setState({
      allProfilesChecked: e.target.checked,
      profilesChecked: e.target.checked ? this.getAllProfilesIds(this.state.cnpjDados) : {},
    });
  }

  handleProfileCheck = e => {
    const { checked, value } = e.target;
    const checkedLength = compact(values(this.state.profilesChecked)).length;

    this.setState(prevState => ({
      allProfilesChecked: checked && checkedLength === this.state.cnpjDados.empresa.agents[0].agentProfiles.length - 1,
      profilesChecked: { ...prevState.profilesChecked, [value]: checked },
    }));
  }

  handleSearch = selected => {
    if (!selected) return;

    let request;

    this.setState({ cnpjDados: undefined, error: undefined, loading: true });

    if (selected.id === "CATIVO") {
      request = getCativoInfo(removeNonDigits(selected.cnpj));
    } else {
      request = getCompanyInfo(selected.id);
    }

    request.then(res => {
      this.setState({
        allProfilesChecked: true,
        cnpjDados: res.data,
        error: undefined,
        loading: false,
        profilesChecked: this.getAllProfilesIds(res.data),
      });
    }).catch(() => this.setState({
      cnpjDados: undefined,
      error: 'Nenhuma empresa encontrada',
      loading: false,
    }));
  }

  ehCativo() {
    const { agents } = this.state.cnpjDados.empresa;
    // agents.length === 0 significa empresa no cativo.
    return agents.length === 0;
  }

  ehConsumidor() {
    const { agents } = this.state.cnpjDados.empresa;
    return some(agents[0].agentProfiles, profile => includes(toLower(profile.class), "consumidor"));
  }

  ehCativoOuConsumidor() {
    if (this.ehCativo() || this.ehConsumidor()) {
      return true;
    }

    return false;
  }

  render() {
    const { classes, simplified } = this.props;
    const { allProfilesChecked, cnpjDados, error, loading, profilesChecked } = this.state;
    let calculatedValuesFromProfiles;

    if (cnpjDados && cnpjDados.empresa.agents.length) {
      calculatedValuesFromProfiles = this.getConsolidatedFromProfiles();
    }

    const agentProfile = get(cnpjDados, 'empresa.agents[0].agentProfiles[0]', {});

    return (
      <div ref={node => this.dashboard = node}>
        {!simplified && (
          <div className={classes.autosuggestWrapper}>
            <AutosuggestCNPJ defaultValue={get(cnpjDados, "empresa.name", "")} onHandleSelected={this.handleSearch} />
          </div>
        )}
        {!simplified && error && (
          <h3>{error}</h3>
        )}
        {loading && (
          <LinearProgress />
        )}
        {cnpjDados && (
          <div ref={node => this.dashboardLoaded = node}>
            <Informations empresa={cnpjDados.empresa} />
            {cnpjDados.empresa.address && (
              <Mapa address={cnpjDados.empresa.address} />
            )}
            {cnpjDados.empresa.companyResponsibles.length > 0 && (
              <CorporateStructure companyResponsibles={cnpjDados.empresa.companyResponsibles} selectEmpresa={this.handleSearch} />
            )}
            {this.ehCativoOuConsumidor() && (
              <BreakEven
                agentesDistribuidores={cnpjDados.agentesDistribuidores}
                companyId={cnpjDados.empresa.id}
                distribuidoraSugeridaId={cnpjDados.empresa.idDistribuidoraSugerida}
              />
            )}
            {cnpjDados.empresa.agents.length > 0 && (
              <div>
                <CCEEData
                  agent={cnpjDados.empresa.agents[0]}
                  allProfilesChecked={allProfilesChecked}
                  handleAllProfilesCheck={this.handleAllProfilesCheck}
                  handleProfileCheck={this.handleProfileCheck}
                  profilesChecked={profilesChecked}
                />
                <CargaEGeracao companyId={cnpjDados.empresa.id} />
                <ConsumptionChart consolidatedEnergy={calculatedValuesFromProfiles.energy} />
                {this.ehConsumidor() && (
                  <TabelaBalanco balancoDados={calculatedValuesFromProfiles.balanco} />
                )}
                <MediaMovel
                  agentProfile={agentProfile}
                  checkedProfiles={this.getCheckedProfiles()}
                  consolidatedEnergy={calculatedValuesFromProfiles.energy}
                  pld={cnpjDados.pldMedioPorSubmercado}
                />
                {cnpjDados.empresa.dadosRisco && (
                  <RiscoChart data={cnpjDados.empresa.dadosRisco} />
                )}
                {this.ehCativoOuConsumidor() && (
                  <SazoFlex checkedAgentProfiles={this.getCheckedProfiles()} selected={this.props.selected} />
                )}
                <ConsolidatedAccounting consolidatedAccountingValues={calculatedValuesFromProfiles.accounting} />
              </div>
            )}
            {cnpjDados.empresa.dre && (
              <DRE dre={cnpjDados.empresa.dre} />
            )}
            {this.ehCativoOuConsumidor() && cnpjDados.empresa.cnae && (
              <CNAE cnae={cnpjDados.empresa.cnae} />
            )}
          </div>
        )}
      </div>
    );
  }
}

Dashboard.propTypes = {
  classes: PropTypes.object.isRequired,
  selected: PropTypes.object,
  simplified: PropTypes.bool,
};

export { Dashboard as DashboardComponent };
export default withStyles(styles)(Dashboard);



// WEBPACK FOOTER //
// ./src/views/Dashboard/Dashboard.jsx